// SecuritySection — Settings page "Security" panel
//
// Wraps three flows:
//  1. Backup Email (Add / pending / verified)  — Phase 2b §7
//  2. Phone Number Change (requires verified email) — Phase 2b §8
//  3. Delete Account (30-day soft delete) — Phase 2a §6
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, Mail, Phone, Loader2 } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { api, logout } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { formatPhoneWithFlag } from "../lib/formatPhone";

const COUNTRY_PRIORITY = ["CU", "CA", "RU", "DE", "FR", "IT", "ES", "MX", "GB", "US", "AR"];

export default function SecuritySection({ user, onChanged }) {
  const { lang } = useI18n();
  const [emailState, setEmailState] = useState({ state: "none", email: null }); // none | pending | verified
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [phoneChangeOpen, setPhoneChangeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/email").then(({ data }) => {
      if (!cancelled) setEmailState({ state: data.state || "none", email: data.email });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    try { const { data } = await api.get("/me/email"); setEmailState({ state: data.state || "none", email: data.email }); onChanged?.(); } catch { /* ignore */ }
  };

  const T = (en, es) => (lang === "es" ? es : en);

  return (
    <section className="mx-5 mt-5 lux-card overflow-hidden" data-testid="security-section">
      <div className="px-5 py-4 border-b border-gold-soft">
        <div className="eyebrow">{T("Security", "Seguridad")}</div>
        <h3 className="mt-1 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 20 }}>{T("Account Security", "Seguridad de Cuenta")}</h3>
      </div>

      {/* Backup Email row */}
      <div className="px-5 py-4 border-b border-gold-soft" data-testid="security-email-row">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(232,184,75,0.10)" }}>
            <Mail className="w-4 h-4 text-gold" strokeWidth={1.5}/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 16 }}>{T("Backup Email", "Correo de Respaldo")}</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", marginTop: 2 }}>
              {T("Log in from any device or recover your account", "Iniciar sesión desde cualquier dispositivo")}
            </div>
            {emailState.state === "pending" && (
              <div className="mt-2 inline-flex items-center gap-2" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, padding: "6px 10px", color: "#fcd34d", fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>
                {T("Check your email for a verification link", "Revisa tu correo para verificar")}
                <button onClick={() => setAddEmailOpen(true)} className="text-gold underline-offset-2" data-testid="security-email-resend">
                  {T("Resend", "Reenviar")}
                </button>
              </div>
            )}
            {emailState.state === "verified" && emailState.email && (
              <div className="mt-2 inline-flex items-center gap-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#F5F1E8" }}>
                <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2}/>{emailState.email}
                <button onClick={() => setAddEmailOpen(true)} className="ml-2 text-gold" data-testid="security-email-change">
                  {T("Change", "Cambiar")}
                </button>
              </div>
            )}
            {emailState.state === "none" && (
              <button onClick={() => setAddEmailOpen(true)} data-testid="security-email-add" className="mt-3 inline-flex items-center justify-center" style={{ background: "transparent", border: "1.5px solid rgba(232,184,75,0.5)", color: "#E8B84B", padding: "10px 18px", borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, minHeight: 48 }}>
                {T("Add Email", "Agregar Correo")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Phone row */}
      <div className="px-5 py-4 border-b border-gold-soft" data-testid="security-phone-row">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(232,184,75,0.10)" }}>
            <Phone className="w-4 h-4 text-gold" strokeWidth={1.5}/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 16 }}>{T("Phone Number", "Número de Teléfono")}</div>
            <div className="mt-2 inline-flex items-center gap-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#F5F1E8" }}>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2}/>{user?.phone_e164 ? formatPhoneWithFlag(user.phone_e164) : "—"}
              <button onClick={() => setPhoneChangeOpen(true)} className="ml-2 text-gold" data-testid="security-phone-change">
                {T("Change", "Cambiar")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account row */}
      <div className="px-5 py-4" data-testid="security-delete-row">
        <button
          onClick={() => setDeleteOpen(true)}
          data-testid="security-delete-btn"
          className="w-full text-left flex items-center"
          style={{ minHeight: 48, color: "#ef4444", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}
        >
          {T("Delete Account", "Eliminar Cuenta")}
        </button>
      </div>

      {addEmailOpen && <AddEmailSheet open={addEmailOpen} onClose={() => setAddEmailOpen(false)} onDone={refresh} currentEmail={emailState.email} />}
      {phoneChangeOpen && <PhoneChangeFlow open={phoneChangeOpen} onClose={() => setPhoneChangeOpen(false)} user={user} hasEmail={emailState.state === "verified"} />}
      {deleteOpen && <DeleteAccountSheet open={deleteOpen} onClose={() => setDeleteOpen(false)} />}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Add Email bottom sheet
// ──────────────────────────────────────────────────────────────────────────────
function AddEmailSheet({ open, onClose, onDone, currentEmail }) {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const T = (en, es) => (lang === "es" ? es : en);

  const send = async () => {
    if (!validEmail) return;
    setSending(true);
    try {
      const { data } = await api.post("/auth/email/start", { email, purpose: "add", language: lang });
      if (data.rate_limited) {
        toast.error(T("Too many requests — wait an hour", "Demasiados intentos — espera una hora"));
        return;
      }
      toast.success(T("Check your email for the verification link", "Revisa tu correo para verificar"));
      if (data.mock_link) console.log("[Habano dev] email verify link:", data.mock_link);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  if (!open) return null;
  return createPortal(
    <div data-testid="add-email-sheet" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", background: "#171717", borderTopLeftRadius: 24, borderTopRightRadius: 24, borderTop: "1px solid rgba(232,184,75,0.3)", padding: "10px 18px 24px" }}>
        <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 16px" }} />
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", color: "#B0AAA3", padding: 6 }}><X className="w-5 h-5"/></button>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", textAlign: "center", marginBottom: 8 }}>
          {currentEmail ? T("Change backup email", "Cambiar correo de respaldo") : T("Add backup email", "Agregar correo de respaldo")}
        </h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", textAlign: "center", marginBottom: 16 }}>
          {T("We will send a verification link to this address.", "Te enviaremos un enlace de verificación a esta dirección.")}
        </p>
        <input
          type="email"
          autoFocus
          data-testid="add-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{ width: "100%", minHeight: 48, padding: "0 14px", background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 10, color: "#F5F1E8", fontFamily: "'DM Sans', sans-serif", fontSize: 16, marginBottom: 14 }}
        />
        <button
          onClick={send}
          disabled={!validEmail || sending}
          data-testid="add-email-send"
          style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none", background: !validEmail ? "#3a2e15" : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)", color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700 }}
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin inline" strokeWidth={1.5}/> : T("Send Verification Link", "Enviar Enlace")}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Phone Change multi-step flow
// ──────────────────────────────────────────────────────────────────────────────
function PhoneChangeFlow({ open, onClose, user, hasEmail }) {
  const { lang } = useI18n();
  const [step, setStep] = useState(hasEmail ? "auth" : "needsEmail"); // needsEmail | auth | newPhone | sentWa | done
  const [authToken, setAuthToken] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const T = (en, es) => (lang === "es" ? es : en);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!open) return null;

  const sendAuthEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/phone-change/start", { language: lang });
      if (data.rate_limited) { toast.error(T("Too many requests — wait an hour", "Demasiados intentos")); return; }
      toast.success(T("Check your email for the authorization link", "Revisa tu correo para autorizar"));
      if (data.mock_link) {
        console.log("[Habano dev] phone-change authorize link:", data.mock_link);
        // For dev, auto-call /authorize with the token in mock_link
        try {
          const u = new URL(data.mock_link);
          const tok = u.searchParams.get("token");
          if (tok) {
            const { data: ad } = await api.post("/me/phone-change/authorize", { token: tok });
            setAuthToken(ad.authorize_token);
            setStep("newPhone");
            return;
          }
        } catch { /* ignore */ }
      }
      // Production path: keep showing the auth step until user clicks email link.
      toast.info(T("After clicking the link in your email, return here.", "Después de hacer clic en el enlace, regresa aquí."));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const sendWaLink = async () => {
    if (!isValidPhoneNumber(newPhone || "")) { toast.error(T("Invalid phone", "Número inválido")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/me/phone-change/verify", { authorize_token: authToken, new_phone_e164: newPhone });
      if (data.whatsapp_url) {
        // Open WhatsApp with the pre-filled magic-link message bound to the new number.
        window.open(data.whatsapp_url, "_blank", "noopener");
      }
      toast.success(T("WhatsApp link sent — open it on the new phone", "Enlace WhatsApp enviado — ábrelo en el nuevo teléfono"));
      if (data.mock_link) console.log("[Habano dev] phone-change verify link:", data.mock_link);
      setStep("sentWa");
      // Optional dev convenience: if no real provider configured (mock_link returned w/o WA send),
      // auto-complete so QA can validate the swap without a real device.
      if (data.mock_link && process.env.NODE_ENV !== "production") {
        const { data: cd } = await api.post("/me/phone-change/complete", { verify_token: data.verify_token });
        try { localStorage.setItem("habano.lastPhone", cd.new_phone_e164); } catch { /* ignore */ }
        toast.success(T("Phone number updated successfully", "Número actualizado correctamente"));
        setStep("done");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div data-testid="phone-change-sheet" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", background: "#171717", borderTopLeftRadius: 24, borderTopRightRadius: 24, borderTop: "1px solid rgba(232,184,75,0.3)", padding: "10px 18px 24px" }}>
        <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 16px" }} />
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", color: "#B0AAA3", padding: 6 }}><X className="w-5 h-5"/></button>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", textAlign: "center", marginBottom: 12 }}>{T("Change phone number", "Cambiar número")}</h2>

        {step === "needsEmail" && (
          <div data-testid="phone-needs-email" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 10, padding: 14, marginBottom: 14, color: "#fcd34d", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
            {T("Add a verified backup email first — this protects your account during phone changes.", "Agrega un correo de respaldo verificado primero — esto protege tu cuenta.")}
          </div>
        )}
        {step === "auth" && (
          <>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#D8D2BF", textAlign: "center", marginBottom: 18 }}>
              {T("We will send an authorization link to your verified email.", "Enviaremos un enlace de autorización a tu correo verificado.")}
            </p>
            <button
              onClick={sendAuthEmail}
              disabled={busy}
              data-testid="phone-send-auth-email"
              style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none", background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)", color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700 }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" strokeWidth={1.5}/> : T("Send Authorization Link", "Enviar Enlace de Autorización")}
            </button>
          </>
        )}
        {step === "newPhone" && (
          <>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#D8D2BF", textAlign: "center", marginBottom: 18 }}>
              {T("Enter your new phone number.", "Ingresa tu nuevo número de teléfono.")}
            </p>
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
              <PhoneInput defaultCountry="CU" value={newPhone} onChange={(v) => setNewPhone(v || "")} international countryCallingCodeEditable={false} countries={[...COUNTRY_PRIORITY]} addInternationalOption={false} style={{ color: "#F5F1E8" }} />
            </div>
            <button onClick={sendWaLink} disabled={busy || !isValidPhoneNumber(newPhone || "")} data-testid="phone-send-wa" style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none", background: !isValidPhoneNumber(newPhone || "") ? "#3a2e15" : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)", color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700 }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" strokeWidth={1.5}/> : T("Send WhatsApp Link", "Enviar Enlace WhatsApp")}
            </button>
          </>
        )}
        {step === "sentWa" && (
          <p data-testid="phone-sent-wa" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#D8D2BF", textAlign: "center" }}>
            {T("Open the WhatsApp link on the new phone to complete the change.", "Abre el enlace de WhatsApp en el nuevo teléfono para completar el cambio.")}
          </p>
        )}
        {step === "done" && (
          <p data-testid="phone-done" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#10b981", textAlign: "center" }}>
            <CheckCircle2 className="inline w-4 h-4 mr-1"/> {T("Phone updated", "Número actualizado")}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Delete Account confirm sheet
// ──────────────────────────────────────────────────────────────────────────────
function DeleteAccountSheet({ open, onClose }) {
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const T = (en, es) => (lang === "es" ? es : en);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!open) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/me/account/deactivate");
      try {
        for (const k of Object.keys(localStorage)) {
          if (/auth|session|habano\.user|habano\.lastPhone/.test(k)) localStorage.removeItem(k);
        }
      } catch { /* ignore */ }
      try { await logout(); } catch { /* ignore */ }
      toast.success(T("Your account is scheduled for deletion", "Tu cuenta está programada para eliminarse"));
      window.location.assign("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div data-testid="delete-account-sheet" role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 210, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.72)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 380, background: "#171717", borderRadius: 18, border: "1px solid rgba(232,184,75,0.3)", padding: "22px 20px 18px" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", marginBottom: 10 }}>{T("Delete your account?", "¿Eliminar tu cuenta?")}</h3>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.45, marginBottom: 18 }}>
          {T(
            "This cannot be undone. Your data will be permanently deleted in 30 days. You can reactivate by logging in before then.",
            "Esto no se puede deshacer. Tus datos se eliminarán permanentemente en 30 días. Puedes reactivar iniciando sesión antes de esa fecha.",
          )}
        </p>
        <button
          onClick={confirm}
          disabled={busy}
          data-testid="delete-account-confirm"
          style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none", background: "#ef4444", color: "#FFFFFF", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, marginBottom: 8 }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" strokeWidth={1.5}/> : T("Delete My Account", "Eliminar Mi Cuenta")}
        </button>
        <button
          onClick={onClose}
          data-testid="delete-account-cancel"
          style={{ width: "100%", minHeight: 48, borderRadius: 10, background: "transparent", border: "1.5px solid rgba(232,184,75,0.5)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}
        >
          {T("Cancel", "Cancelar")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
